/**
 * AssemblyResultsBanner - Display assembly analyses for a project
 *
 * Shows a banner between the filter bar and file cards listing all
 * assembly analyses performed on the project. Clicking on an analysis
 * navigates to the full page view.
 */

import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { IoCubeOutline } from 'react-icons/io5';
import { FiChevronRight, FiPackage, FiClock } from 'react-icons/fi';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;

export default function AssemblyResultsBanner({ folderName, spacePath }) {
    const [analyses, setAnalyses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const history = useHistory();
    const token = sessionStorage.getItem("userToken");

    useEffect(() => {
        if (!folderName) return;

        const fetchAssemblies = async () => {
            try {
                setLoading(true);
                const params = new URLSearchParams({
                    folder_name: folderName,
                    ...(spacePath && { space_path: spacePath })
                });

                const res = await axios.get(
                    `${BASE_URL}/ocr/assembly-analyses/?${params}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                // / Add this debug logging:
                console.log('🔍 Assembly API FULL Response:', res);
                console.log('📊 Response data:', JSON.stringify(res.data, null, 2));
                console.log('📊 Analyses count:', res.data.analyses?.length || 0);
                console.log('👤 Current user role:', sessionStorage.getItem('userRole'));
                console.log('📁 Folder name:', folderName);
                console.log('🗂️ Space path:', spacePath);
                setAnalyses(res.data.analyses || []);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch assembly analyses:', err);
                setError('Failed to load assembly analyses');
            } finally {
                setLoading(false);
            }
        };

        fetchAssemblies();
    }, [folderName, spacePath, token]);

    const formatTimestamp = (ts) => {
        if (!ts) return 'Unknown date';
        // Format: 20241225_143022 -> Dec 25, 2024 2:30 PM (in user's local timezone)
        // Server timestamp is in UTC
        try {
            const year = ts.substring(0, 4);
            const month = ts.substring(4, 6);
            const day = ts.substring(6, 8);
            const hour = ts.substring(9, 11);
            const minute = ts.substring(11, 13);
            const second = ts.substring(13, 15) || '00';

            // Create date in UTC, then format will convert to user's local timezone
            const utcDate = new Date(Date.UTC(
                parseInt(year),
                parseInt(month) - 1,
                parseInt(day),
                parseInt(hour),
                parseInt(minute),
                parseInt(second)
            ));

            return utcDate.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                timeZoneName: 'short'
            });
        } catch {
            return ts;
        }
    };

    const handleViewAssembly = (analysis) => {
        history.push({
            pathname: `/assembly-results/${analysis.id}`,
            state: {
                analysis,
                folderName,
                spacePath
            }
        });
    };

    // Don't render if no analyses or loading
    if (loading) return null;
    if (!analyses.length) return null;

    return (
        <div className="mb-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-green-800 to-green-700 flex items-center gap-3">
                <IoCubeOutline className="w-5 h-5 text-white" />
                <h3 className="font-semibold text-white">Assembly Analyses</h3>
                <span className="ml-auto text-green-100 text-sm">
                    {analyses.length} {analyses.length === 1 ? 'analysis' : 'analyses'}
                </span>
            </div>

            {/* Analysis Cards */}
            <div className="p-3 space-y-2">
                {analyses.map((analysis) => (
                    <button
                        key={analysis.id}
                        onClick={() => handleViewAssembly(analysis)}
                        className="w-full flex items-center gap-4 p-3 bg-white rounded-lg border border-gray-100
                                   hover:border-green-300 hover:shadow-sm transition-all group text-left"
                    >
                        {/* Icon */}
                        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                            <FiPackage className="w-5 h-5 text-green-700" />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">
                                    {analysis.play_id === 'tariff_v1' ? 'Tariff Analysis'
                                        : analysis.play_id === 'tariff_v2' ? 'Tariff Classification v2' : 'Cost Reduction'}
                                </span>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                    {analysis.component_count} components
                                </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-500 mt-0.5">
                                <FiClock className="w-3.5 h-3.5" />
                                <span>{formatTimestamp(analysis.timestamp)}</span>
                            </div>
                            {/* Summary preview */}
                            {analysis.summary?.primary_material && (
                                <div className="text-xs text-gray-500 mt-1 truncate">
                                    Primary material: {analysis.summary.primary_material}
                                </div>
                            )}
                        </div>

                        {/* Arrow */}
                        <FiChevronRight className="w-5 h-5 text-gray-400 group-hover:text-green-600 transition-colors" />
                    </button>
                ))}
            </div>

            {error && (
                <div className="px-4 pb-3 text-sm text-red-600">
                    {error}
                </div>
            )}
        </div>
    );
}
